import { api } from "./http";

/**
 * Información de la empresa del representante (nombre, código Excel único).
 */
export async function getCompanyInfo() {
	const res = await api.get("/api/company/info/");
	return res.data;
}

/**
 * Lista contratos/cursos disponibles para la empresa del usuario (CompanyAdmin).
 */
export async function listCompanyContracts() {
	const res = await api.get("/api/company/contracts/");
	return res.data;
}

/**
 * Lista solo los lotes que subió este representante (aislamiento por uploaded_by).
 */
export async function listMyBatches() {
	const res = await api.get("/api/company/my-batches/");
	return res.data;
}

/**
 * Lista estudiantes de un lote. Solo para lotes propios del representante.
 */
export async function listBatchStudents(batchId) {
	const res = await api.get(`/api/company/my-batches/${batchId}/students/`);
	return res.data;
}

/**
 * Detalle por fila del Excel al subir el lote (created/updated/reassigned/skipped).
 */
export async function listBatchRows(batchId) {
	const res = await api.get(`/api/company/my-batches/${batchId}/rows/`);
	return res.data;
}

/**
 * Actualiza estudiante: is_active y/o first_name, last_name, email, phone.
 */
export async function updateStudent(studentId, payload) {
	const res = await api.patch(`/api/company/my-students/${studentId}/`, payload);
	return res.data;
}

/**
 * Deshabilita o habilita un estudiante (solo si está en uno de tus lotes).
 */
export async function setStudentActive(studentId, isActive) {
	return updateStudent(studentId, { is_active: isActive });
}

/**
 * Asigna contraseña simple al estudiante (inicial + apellidos + RUT). Devuelve temp_password.
 */
export async function setStudentSimplePassword(studentId) {
	const res = await api.patch(`/api/company/my-students/${studentId}/`, { set_simple_password: true });
	return res.data;
}

/**
 * Elimina un lote (planilla). Los estudiantes no se borran.
 */
export async function deleteBatch(batchId) {
	await api.delete(`/api/company/my-batches/${batchId}/`);
}

/**
 * Asigna contraseña simple a todos los estudiantes del lote.
 */
export async function setBatchPasswords(batchId) {
	const res = await api.post(`/api/company/my-batches/${batchId}/set-passwords/`);
	return res.data;
}

/**
 * Descarga un Excel con todas las contraseñas del lote (RUT, Nombres, Apellidos, Usuario, Contraseña_temporal).
 * Usa la misma fórmula que "Asignar contraseñas a todo el lote"; conviene asignar antes y luego descargar.
 */
export async function downloadBatchPasswordsExcel(batchId) {
	try {
		const res = await api.get(`/api/company/my-batches/${batchId}/passwords-excel/`, {
			responseType: "blob",
		});
		const blob = res.data;
		if (!(blob instanceof Blob) || blob.size === 0) throw new Error("No se pudo generar el archivo.");
		const isJson = res.headers?.["content-type"]?.toLowerCase?.().includes("json");
		if (isJson) {
			const text = await new Promise((resolve, reject) => {
				const r = new FileReader();
				r.onload = () => resolve(r.result);
				r.onerror = reject;
				r.readAsText(blob);
			});
			const data = JSON.parse(text);
			throw new Error(data?.error || "Error al generar el Excel.");
		}
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `contraseñas_lote_${batchId}.xlsx`;
		a.style.display = "none";
		document.body.appendChild(a);
		a.click();
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
			a.remove();
		}, 1000);
	} catch (err) {
		if (err.response?.data instanceof Blob) {
			try {
				const text = await err.response.data.text();
				const data = JSON.parse(text);
				throw new Error(data?.error || "No se pudo descargar el Excel.");
			} catch (e) {
				if (e instanceof Error && e.message) throw e;
			}
		}
		throw err;
	}
}

/**
 * Descarga la plantilla Excel (rut, nombres, apellidos, correo, telefono).
 * GET /api/company/template-students/ con Authorization: Bearer.
 */
export async function downloadTemplateStudents() {
	try {
		const res = await api.get("/api/company/template-students/", {
			responseType: "blob",
		});
		const blob = res.data;
		if (!(blob instanceof Blob)) {
			throw new Error("Respuesta inválida del servidor.");
		}
		if (blob.size === 0) {
			throw new Error("El archivo recibido está vacío.");
		}
		// Si el servidor devolvió JSON (error) en un blob
		const isJson = res.headers?.["content-type"]?.toLowerCase?.().includes("json") ||
			(blob.type && blob.type.toLowerCase().indexOf("json") !== -1);
		if (isJson) {
			const text = await new Promise((resolve, reject) => {
				const r = new FileReader();
				r.onload = () => resolve(r.result);
				r.onerror = reject;
				r.readAsText(blob);
			});
			const data = JSON.parse(text);
			const msg = data?.error || data?.detail || "No se pudo descargar la plantilla.";
			throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
		}
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "plantilla_estudiantes_somnus.xlsx";
		a.style.display = "none";
		document.body.appendChild(a);
		a.click();
		// Dar tiempo al navegador para iniciar la descarga antes de revocar el URL
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
			a.remove();
		}, 1000);
	} catch (err) {
		if (err.response?.data instanceof Blob) {
			try {
				const text = await err.response.data.text();
				const data = JSON.parse(text);
				const msg = data?.detail || data?.error || "No se pudo descargar la plantilla.";
				throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
			} catch (e) {
				if (e instanceof Error && e.message && e !== err) throw e;
				const status = err.response?.status;
				throw new Error(status === 403 ? "No tienes permiso para descargar la plantilla. Inicia sesión como representante de empresa." : "No se pudo descargar la plantilla.");
			}
		}
		if (err instanceof Error && err.message) throw err;
		throw new Error("No se pudo descargar la plantilla.");
	}
}

/**
 * Sube Excel para crear un StudentBatch.
 * POST /api/company/upload-students/ (multipart form-data)
 * excel_code: obligatorio, debe coincidir con el de la empresa del representante.
 */
export async function uploadStudents(file, excelCode) {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("excel_code", excelCode ?? "");
	const res = await api.post("/api/company/upload-students/", formData);
	return res.data; // { batch_id, created, updated, reassigned, skipped }
}

/**
 * Matricula el batch en un contrato (CompanyCourse).
 * POST /api/company/enroll-batch/
 */
export async function enrollBatch({ batch_id, company_course_id }) {
	const res = await api.post("/api/company/enroll-batch/", { batch_id, company_course_id });
	return res.data; // { message, students_enrolled }
}

// Función para pedir los datos del representante logueado
export async function getMyProfile() {
    const response = await api.get('/api/company/me/'); 
    return response.data;
}

// Función para guardar los cambios
export async function updateMyProfile(data) {
    const response = await api.patch('/api/company/me/', data);
    return response.data;
}